// Pure helpers for non-destructive lane edits (undo snapshots, range ops).
export const CLIP_KEYS = ["beat_offset_ms", "clip_start_ms", "clip_end_ms", "clip_gain_db", "fade_in_ms", "fade_out_ms"];
const MIN_PIECE_MS = 50;

export function laneSnapshot(takes, trackId) {
  return (takes || []).filter((k) => k.track_id === trackId).map((k) => {
    const o = { take_id: k.take_id };
    for (const f of CLIP_KEYS) o[f] = f === "clip_end_ms" ? (k.clip_end_ms || k.duration_ms || 0) : (k[f] || 0);
    return o;
  });
}

// Range (timeline ms) → full lane entries for op "split" | "trim" | "delete".
export function rangeLaneEntries(takes, range, op) {
  const lane = laneSnapshot(takes, range.trackId);
  const k = lane.find((x) => x.take_id === range.takeId);
  if (!k) return null;
  const cs = k.clip_start_ms;
  const ce = k.clip_end_ms;
  const off = k.beat_offset_ms;
  const la = Math.max(cs, Math.min(ce, Math.round(cs + (range.startMs - off))));
  const lb = Math.max(cs, Math.min(ce, Math.round(cs + (range.endMs - off))));
  if (lb - la < MIN_PIECE_MS) return null;
  const pieces = op === "trim" ? [[la, lb]] : op === "delete" ? [[cs, la], [lb, ce]] : [[cs, la], [la, lb], [lb, ce]];
  const out = pieces.filter(([a, b]) => b - a >= MIN_PIECE_MS).map(([a, b], i) => ({
    ...(i === 0 ? { take_id: k.take_id } : { from_take_id: k.take_id }),
    beat_offset_ms: off + (a - cs), clip_start_ms: a, clip_end_ms: b, clip_gain_db: k.clip_gain_db,
    fade_in_ms: a === cs ? k.fade_in_ms : 0, fade_out_ms: b === ce ? k.fade_out_ms : 0,
  }));
  return [...lane.filter((x) => x.take_id !== k.take_id), ...out];
}

// Punch-in replace: cut [startMs,endMs) out of every OTHER clip on the lane
// (keeps audio before/after). Returns null when nothing overlaps.
export function punchInEntries(takes, trackId, newTakeId, startMs, endMs, canCut = () => true) {
  const lane = laneSnapshot(takes, trackId);
  let changed = false;
  const out = [];
  for (const k of lane) {
    const off = k.beat_offset_ms;
    const tEnd = off + (k.clip_end_ms - k.clip_start_ms);
    if (k.take_id === newTakeId || tEnd <= startMs || off >= endMs || !canCut(k.take_id)) { out.push(k); continue; }
    changed = true;
    const cs = k.clip_start_ms;
    const ce = k.clip_end_ms;
    const la = Math.max(cs, Math.min(ce, Math.round(cs + (startMs - off))));
    const lb = Math.max(cs, Math.min(ce, Math.round(cs + (endMs - off))));
    const pieces = [[cs, la], [lb, ce]].filter(([a, b]) => b - a >= MIN_PIECE_MS);
    pieces.forEach(([a, b], i) => out.push({
      ...(i === 0 ? { take_id: k.take_id } : { from_take_id: k.take_id }),
      beat_offset_ms: off + (a - cs), clip_start_ms: a, clip_end_ms: b, clip_gain_db: k.clip_gain_db,
      fade_in_ms: a === cs ? k.fade_in_ms : 8, fade_out_ms: b === ce ? k.fade_out_ms : 8,
    }));
  }
  return changed ? out : null;
}


export const snapTo = (ms, grid) => (grid > 0 ? Math.round(ms / grid) * grid : ms);
